import React, {
  createContext,
  CSSProperties,
  PropsWithChildren,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { ConfigProvider, ConfigProviderProps } from "antd"

export type DashboardLayoutMode =
  | "comfortable"
  | "comfortable-lg"
  | "comfortable-xl"
  | "compact-1280"
  | "compact-1366"
  | "dense"
  | "fallback"

export type DashboardLayoutConfig = {
  buttonPaddingInline: number
  cardPadding: number
  componentSize: "middle" | "small"
  filterSpan: number
  fontSize: number
  formItemMarginBottom: number
  gutter: [number, number]
  headerHeight: number
  headerPadding: number
  navMinHeight: number
  pagePadding: number
  shellGap: number
  tableCellPaddingBlock: number
  tableCellPaddingInline: number
  tableFontSize: number
  tableScrollX?: number
  tableSize: "middle" | "small"
  tabsGutter: number
  titleFontSize: number
}

export const DASHBOARD_LAYOUTS: Record<
  DashboardLayoutMode,
  DashboardLayoutConfig
> = {
  "comfortable": {
    buttonPaddingInline: 15,
    cardPadding: 20,
    componentSize: "middle",
    filterSpan: 6,
    fontSize: 14,
    formItemMarginBottom: 12,
    gutter: [12, 12],
    headerHeight: 42,
    headerPadding: 20,
    navMinHeight: 44,
    pagePadding: 20,
    shellGap: 20,
    tableCellPaddingBlock: 8,
    tableCellPaddingInline: 10,
    tableFontSize: 14,
    tableSize: "middle",
    tabsGutter: 24,
    titleFontSize: 20,
  },
  "comfortable-lg": {
    buttonPaddingInline: 15,
    cardPadding: 22,
    componentSize: "middle",
    filterSpan: 6,
    fontSize: 14,
    formItemMarginBottom: 12,
    gutter: [14, 12],
    headerHeight: 42,
    headerPadding: 22,
    navMinHeight: 46,
    pagePadding: 22,
    shellGap: 22,
    tableCellPaddingBlock: 10,
    tableCellPaddingInline: 10,
    tableFontSize: 14,
    tableSize: "middle",
    tabsGutter: 26,
    titleFontSize: 20,
  },
  "comfortable-xl": {
    buttonPaddingInline: 15,
    cardPadding: 24,
    componentSize: "middle",
    filterSpan: 6,
    fontSize: 14,
    formItemMarginBottom: 14,
    gutter: [16, 14],
    headerHeight: 44,
    headerPadding: 24,
    navMinHeight: 48,
    pagePadding: 24,
    shellGap: 24,
    tableCellPaddingBlock: 10,
    tableCellPaddingInline: 12,
    tableFontSize: 14,
    tableSize: "middle",
    tabsGutter: 28,
    titleFontSize: 20,
  },
  "compact-1280": {
    buttonPaddingInline: 11,
    cardPadding: 16,
    componentSize: "small",
    filterSpan: 6,
    fontSize: 13,
    formItemMarginBottom: 8,
    gutter: [10, 8],
    headerHeight: 36,
    headerPadding: 16,
    navMinHeight: 40,
    pagePadding: 16,
    shellGap: 16,
    tableCellPaddingBlock: 8,
    tableCellPaddingInline: 8,
    tableFontSize: 14,
    tableScrollX: 1180,
    tableSize: "small",
    tabsGutter: 18,
    titleFontSize: 18,
  },
  "compact-1366": {
    buttonPaddingInline: 13,
    cardPadding: 16,
    componentSize: "middle",
    filterSpan: 6,
    fontSize: 14,
    formItemMarginBottom: 10,
    gutter: [10, 10],
    headerHeight: 38,
    headerPadding: 16,
    navMinHeight: 40,
    pagePadding: 16,
    shellGap: 16,
    tableCellPaddingBlock: 8,
    tableCellPaddingInline: 8,
    tableFontSize: 14,
    tableScrollX: 1180,
    tableSize: "small",
    tabsGutter: 20,
    titleFontSize: 18,
  },
  "dense": {
    buttonPaddingInline: 9,
    cardPadding: 12,
    componentSize: "small",
    filterSpan: 8,
    fontSize: 13,
    formItemMarginBottom: 8,
    gutter: [8, 8],
    headerHeight: 34,
    headerPadding: 12,
    navMinHeight: 36,
    pagePadding: 12,
    shellGap: 12,
    tableCellPaddingBlock: 6,
    tableCellPaddingInline: 8,
    tableFontSize: 13,
    tableScrollX: 1180,
    tableSize: "small",
    tabsGutter: 14,
    titleFontSize: 17,
  },
  "fallback": {
    buttonPaddingInline: 9,
    cardPadding: 12,
    componentSize: "small",
    filterSpan: 12,
    fontSize: 13,
    formItemMarginBottom: 8,
    gutter: [8, 8],
    headerHeight: 34,
    headerPadding: 12,
    navMinHeight: 36,
    pagePadding: 12,
    shellGap: 12,
    tableCellPaddingBlock: 6,
    tableCellPaddingInline: 8,
    tableFontSize: 13,
    tableScrollX: 1180,
    tableSize: "small",
    tabsGutter: 14,
    titleFontSize: 17,
  },
}

export const getDashboardLayoutMode = (width: number): DashboardLayoutMode => {
  if (width >= 1920) return "comfortable-xl"

  if (width >= 1536) return "comfortable-lg"

  if (width >= 1440) return "comfortable"

  if (width >= 1366) return "compact-1366"

  if (width >= 1280) return "compact-1280"

  if (width >= 1024) return "dense"

  return "fallback"
}

type DashboardLayoutContextValue = {
  layout: DashboardLayoutConfig
  mode: DashboardLayoutMode
  width: number
}

const DashboardLayoutContext = createContext<DashboardLayoutContextValue>({
  layout: DASHBOARD_LAYOUTS.comfortable,
  mode: "comfortable",
  width: 1440,
})

export const useDashboardLayout = () => useContext(DashboardLayoutContext)

export const useDashboardContainerWidth = () => {
  const ref = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(() => window.innerWidth)

  useLayoutEffect(() => {
    const element = ref.current

    if (!element) return undefined

    const updateWidth = () => {
      setWidth(Math.round(element.getBoundingClientRect().width))
    }

    updateWidth()

    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? undefined
        : new ResizeObserver(updateWidth)

    resizeObserver?.observe(element)
    window.addEventListener("resize", updateWidth)

    return () => {
      resizeObserver?.disconnect()
      window.removeEventListener("resize", updateWidth)
    }
  }, [])

  return { ref, width }
}

export const DashboardLayoutProvider = ({
  children,
  width,
}: PropsWithChildren<{ width: number }>) => {
  const mode = getDashboardLayoutMode(width)
  const layout = DASHBOARD_LAYOUTS[mode]

  const theme = useMemo<ConfigProviderProps["theme"]>(
    () => ({
      components: {
        Button: {
          contentFontSize: layout.fontSize,
          contentFontSizeLG: layout.fontSize,
          contentFontSizeSM: layout.fontSize,
          paddingInline: layout.buttonPaddingInline,
          paddingInlineLG: layout.buttonPaddingInline,
          paddingInlineSM: layout.buttonPaddingInline,
        },
        Card: {
          bodyPadding: layout.cardPadding,
          bodyPaddingSM: layout.cardPadding,
          headerFontSize: layout.fontSize,
          headerFontSizeSM: layout.fontSize,
          headerHeight: layout.headerHeight,
          headerHeightSM: layout.headerHeight,
          headerPadding: layout.cardPadding,
          headerPaddingSM: layout.cardPadding,
        },
        Form: {
          itemMarginBottom: layout.formItemMarginBottom,
          labelFontSize: layout.fontSize - 1,
          verticalLabelPadding: 0,
        },
        Input: {
          paddingBlock: layout.componentSize === "small" ? 4 : 6,
          paddingBlockLG: layout.componentSize === "small" ? 4 : 6,
          paddingInline: layout.componentSize === "small" ? 7 : 8,
          paddingInlineLG: layout.componentSize === "small" ? 7 : 8,
        },
        Pagination: {
          itemSize: layout.componentSize === "small" ? 32 : 36,
          itemSizeSM: 32,
        },
        Select: {
          optionFontSize: layout.fontSize,
          optionHeight: layout.componentSize === "small" ? 30 : 34,
          optionPadding:
            layout.componentSize === "small" ? "4px 8px" : "6px 8px",
        },
        Table: {
          cellFontSize: layout.tableFontSize,
          cellFontSizeMD: layout.tableFontSize,
          cellFontSizeSM: layout.tableFontSize,
          cellPaddingBlock: layout.tableCellPaddingBlock,
          cellPaddingBlockMD: layout.tableCellPaddingBlock,
          cellPaddingBlockSM: layout.tableCellPaddingBlock,
          cellPaddingInline: layout.tableCellPaddingInline,
          cellPaddingInlineMD: layout.tableCellPaddingInline,
          cellPaddingInlineSM: layout.tableCellPaddingInline,
        },
        Tabs: {
          horizontalItemGutter: layout.tabsGutter,
          horizontalItemPadding:
            layout.componentSize === "small" ? "8px 0" : "10px 0",
          titleFontSize: layout.fontSize,
          titleFontSizeLG: layout.fontSize,
          titleFontSizeSM: layout.fontSize,
        },
      },
      token: {
        borderRadius: 8,
        controlHeight: layout.componentSize === "small" ? 32 : 36,
        controlHeightLG: layout.componentSize === "small" ? 36 : 40,
        controlHeightSM: 32,
        fontSize: layout.fontSize,
      },
    }),
    [layout],
  )

  const value = useMemo(
    () => ({
      layout,
      mode,
      width,
    }),
    [layout, mode, width],
  )

  const style = useMemo<CSSProperties>(
    () =>
      ({
        "--dashboard-card-padding": `${layout.cardPadding}px`,
        "--dashboard-font-size": `${layout.fontSize}px`,
        "--dashboard-header-height": `${layout.headerHeight}px`,
        "--dashboard-header-padding": `${layout.headerPadding}px`,
        "--dashboard-nav-min-height": `${layout.navMinHeight}px`,
        "--dashboard-shell-gap": `${layout.shellGap}px`,
        "--dashboard-table-min-width": `${layout.tableScrollX ?? 0}px`,
        "--dashboard-title-font-size": `${layout.titleFontSize}px`,
      }) as CSSProperties,
    [layout.tableScrollX],
  )

  return (
    <DashboardLayoutContext.Provider value={value}>
      <ConfigProvider componentSize={layout.componentSize} theme={theme}>
        <div className="dashboard-layout-density" style={style}>
          {children}
        </div>
      </ConfigProvider>
    </DashboardLayoutContext.Provider>
  )
}
